import React from 'react';
import { useTranslate } from '@tolgee/react';

import { LomavisStatusCard1 } from '@/lomavisSharedUi/atoms/feedback/LomavisStatusCard1';

import styles from './LomavisMediaUploadPanel1.module.css';

export type LomavisMediaUploadFileKind1 = 'image' | 'video' | 'file';

export type LomavisMediaUploadFile1 = {
  id: string;
  name: string;
  kind?: LomavisMediaUploadFileKind1;
  meta?: string;
  previewUrl?: string;
};

export type LomavisMediaUploadProgress1 = {
  fileName: string;
  percent: number;
};

export type LomavisMediaUploadNoticeLevel1 = 'error' | 'warning' | 'info';

export type LomavisMediaUploadNoticeItem1 = {
  fileName?: string;
  message: string;
};

export type LomavisMediaUploadNotice1 = {
  level: LomavisMediaUploadNoticeLevel1;
  title?: string;
  items: LomavisMediaUploadNoticeItem1[];
};

export interface LomavisMediaUploadPanel1Props {
  onBrowse: () => void;
  onOpenMediaLibrary?: () => void;
  onFilesDrop?: (files: File[]) => void;
  onFileRemove?: (id: string) => void;
  onCancelUpload?: () => void;
  selectedFiles?: LomavisMediaUploadFile1[];
  uploadProgress?: LomavisMediaUploadProgress1 | null;
  notice?: LomavisMediaUploadNotice1 | null;
  disabled?: boolean;
  mediaLibraryDisabled?: boolean;
  className?: string;
}

export const LomavisMediaUploadPanel1: React.FC<
  LomavisMediaUploadPanel1Props
> = ({
  onBrowse,
  onOpenMediaLibrary,
  onFilesDrop,
  onFileRemove,
  onCancelUpload,
  selectedFiles = [],
  uploadProgress = null,
  notice = null,
  disabled = false,
  mediaLibraryDisabled = disabled,
  className = ''
}) => {
  const { t } = useTranslate();
  const dragDepthRef = React.useRef(0);
  const [isDraggingFiles, setIsDraggingFiles] = React.useState(false);
  const progressPercent = Math.max(
    0,
    Math.min(100, Math.round(uploadProgress?.percent ?? 0))
  );

  const hasFileDrag = (event: React.DragEvent<HTMLElement>) =>
    Array.from(event.dataTransfer.types || []).includes('Files');

  const handleBrowse = () => {
    if (disabled) return;
    onBrowse();
  };

  const handleOpenMediaLibrary = () => {
    if (disabled || mediaLibraryDisabled) return;
    onOpenMediaLibrary?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    handleBrowse();
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileDrag(event)) return;

    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;

    dragDepthRef.current += 1;
    setIsDraggingFiles(true);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileDrag(event)) return;

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
    if (disabled) return;

    setIsDraggingFiles(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileDrag(event)) return;

    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;

    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDraggingFiles(false);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    if (!hasFileDrag(event)) return;

    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);

    if (disabled) return;

    const files = Array.from(event.dataTransfer.files || []);
    if (files.length > 0) {
      onFilesDrop?.(files);
    }
  };

  return (
    <div className={`d-flex flex-column gap-3 ${className}`.trim()}>
      {notice && notice.items.length > 0 && (
        <LomavisStatusCard1
          variant={
            notice.level === 'error'
              ? 'danger'
              : notice.level === 'warning'
              ? 'warning'
              : 'info'
          }
          title={notice.title}
          description={
            notice.items.length === 1 ? (
              <>
                {notice.items[0].fileName && (
                  <strong>{notice.items[0].fileName}: </strong>
                )}
                {notice.items[0].message}
              </>
            ) : (
              <ul className="mb-0 ps-3">
                {notice.items.map((item, index) => (
                  <li key={`${item.fileName ?? ''}-${index}`}>
                    {item.fileName && <strong>{item.fileName}: </strong>}
                    {item.message}
                  </li>
                ))}
              </ul>
            )
          }
        />
      )}
    <div
      className={`${
        styles.panel
      } d-flex flex-column align-items-center justify-content-center gap-3 rounded-3 p-4 text-center ${
        disabled ? styles.disabled : ''
      } ${isDraggingFiles ? styles.dragActive : ''} ${
        !disabled ? styles.clickable : ''
      }`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t('adManager.media.uploadMedia', 'Upload media')}
      aria-disabled={disabled}
      onClick={handleBrowse}
      onKeyDown={handleKeyDown}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <span
        className={`${styles.icon} d-inline-flex align-items-center justify-content-center rounded-circle`}
        aria-hidden="true"
      >
        <i className="fa-regular fa-image" />
      </span>
      <div>
        <div className="fw-bold text-body">
          {t('adManager.media.uploadMedia', 'Upload media')}
        </div>
        <div
          className="mx-auto text-muted small fw-medium lh-base"
          style={{ maxWidth: 360 }}
        >
          {t(
            'adManager.media.dropOrBrowse',
            'Drop your media on the canvas or browse from your device.'
          )}
        </div>
      </div>
      <div className="d-flex flex-wrap justify-content-center gap-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          disabled={disabled}
          onClick={(event) => {
            event.stopPropagation();
            handleBrowse();
          }}
        >
          {t('adManager.media.browse', 'Browse')}
        </button>
        {onOpenMediaLibrary && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            disabled={disabled || mediaLibraryDisabled}
            onClick={(event) => {
              event.stopPropagation();
              handleOpenMediaLibrary();
            }}
          >
            <i className="fa-duotone fa-photo-film me-2" aria-hidden="true" />
            {t('adManager.media.mediaLibrary', 'Media library')}
          </button>
        )}
      </div>
      {uploadProgress && (
        <div
          className={`${styles.progressWrap} d-grid gap-2 text-start`}
          aria-live="polite"
        >
          <div className="d-flex align-items-center justify-content-between gap-3 small fw-bold">
            <span className="text-truncate">{uploadProgress.fileName}</span>
            <span className="d-flex align-items-center gap-2 flex-shrink-0">
              <span>{progressPercent}%</span>
              {onCancelUpload && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary py-0 px-2"
                  onClick={(event) => {
                    event.stopPropagation();
                    onCancelUpload();
                  }}
                >
                  {t('common.cancel', 'Cancel')}
                </button>
              )}
            </span>
          </div>
          <div
            className="progress"
            role="progressbar"
            aria-label={t(
              'adManager.media.uploadingFile',
              'Uploading {fileName}',
              { fileName: uploadProgress.fileName }
            )}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progressPercent}
          >
            <div
              className="progress-bar"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}
      {selectedFiles.length > 0 && (
        <ul
          className="d-grid list-unstyled gap-2 m-0 p-0"
          aria-label={t(
            'adManager.media.selectedFiles',
            'Selected media files'
          )}
          style={{ width: 'min(100%, 360px)' }}
        >
          {selectedFiles.map((file) => (
            <li
              key={file.id}
              className={`${styles.fileItem} align-items-center gap-2 rounded-3 border bg-white px-2 py-2 text-start text-body small`}
            >
              <span
                className={`${styles.fileThumb} d-inline-flex align-items-center justify-content-center overflow-hidden rounded-2`}
                aria-hidden="true"
              >
                {file.previewUrl && file.kind === 'image' ? (
                  <img src={file.previewUrl} alt="" />
                ) : file.previewUrl && file.kind === 'video' ? (
                  <video src={file.previewUrl} muted playsInline />
                ) : (
                  <i className="fa-regular fa-file-image" />
                )}
              </span>
              <span className="d-grid gap-1" style={{ minWidth: 0 }}>
                <span className="text-truncate fw-bold">{file.name}</span>
                {file.meta && (
                  <span className="text-muted small fw-semibold">
                    {file.meta}
                  </span>
                )}
              </span>
              {onFileRemove && (
                <button
                  type="button"
                  className={`${styles.removeButton} d-inline-flex align-items-center justify-content-center rounded-circle border bg-white p-0 text-muted small`}
                  disabled={disabled}
                  aria-label={t(
                    'adManager.media.removeFile',
                    'Remove {fileName}',
                    { fileName: file.name }
                  )}
                  onClick={(event) => {
                    event.stopPropagation();
                    onFileRemove(file.id);
                  }}
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div
        className="mx-auto text-muted small fw-medium lh-base"
        style={{ maxWidth: 360 }}
      >
        {t(
          'adManager.media.recommendation',
          'Recommended: 1080x1080 or 1080x1920 - JPEG, PNG, MP4, MOV - up to 30 MB.'
        )}
      </div>
    </div>
    </div>
  );
};
